import { useEffect, useRef } from "react";
import type { ReactElement, ReactNode } from "react";
import { Box, SimpleGrid } from "@chakra-ui/react";
import type { SimpleGridProps } from "@chakra-ui/react";
import { Button, EmptyState } from "pixelize-design-library";
import {
  ErrorState,
  SkeletonAddressCard,
  SkeletonBrandTile,
  SkeletonCategoryTile,
  SkeletonProductGridCard,
  SkeletonProductRow,
} from "@core/components";

export type SkeletonVariant = "product" | "productRow" | "brand" | "category" | "address";

const SKELETON_COUNT = 8;

const SKELETON_COMPONENT: Record<SkeletonVariant, () => ReactElement> = {
  product: () => <SkeletonProductGridCard compact />,
  productRow: SkeletonProductRow,
  brand: SkeletonBrandTile,
  category: SkeletonCategoryTile,
  address: SkeletonAddressCard,
};

export type DataListProps<T> = {
  data: readonly T[] | undefined;
  renderItem: (item: T, index: number) => ReactNode;
  keyExtractor: (item: T, index: number) => string;
  isLoading: boolean;
  error?: unknown;
  onRetry?: () => void;
  onEndReached?: () => void;
  hasMore?: boolean;
  header?: ReactNode;
  skeletonHeader?: ReactNode;
  skeletonVariant?: SkeletonVariant;
  footer?: ReactNode;
  columns?: SimpleGridProps["columns"];
  gap?: SimpleGridProps["gap"];
  emptyIcon?: React.ElementType;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  testID?: string;
};

export function DataList<T>({
  data,
  renderItem,
  keyExtractor,
  isLoading,
  error,
  onRetry,
  onEndReached,
  hasMore = false,
  header,
  skeletonHeader,
  skeletonVariant = "product",
  footer,
  columns = 1,
  gap = { base: 2, md: 3, lg: 4 },
  emptyIcon,
  emptyTitle = "Nothing here yet",
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  testID,
}: DataListProps<T>): ReactElement {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onEndReached === undefined || !hasMore || sentinelRef.current === null) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting === true) onEndReached();
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [onEndReached, hasMore]);

  if (isLoading && data === undefined) {
    const SkeletonItem = SKELETON_COMPONENT[skeletonVariant];
    return (
      <Box data-testid={testID}>
        {skeletonHeader}
        <SimpleGrid columns={columns} gap={gap} alignItems="start" w="100%">
          {Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <Box key={index} minW={0} h={skeletonVariant === "product" ? "100%" : undefined}>
              <SkeletonItem />
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    );
  }
  if (error !== undefined && error !== null) return <ErrorState error={error} onRetry={onRetry} />;
  if (data !== undefined && data.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        icon={emptyIcon}
        action={
          emptyActionLabel === undefined || onEmptyAction === undefined ? undefined : (
            <Button label={emptyActionLabel} onClick={onEmptyAction} />
          )
        }
      />
    );
  }

  return (
    <Box data-testid={testID}>
      {header}
      <SimpleGrid columns={columns} gap={gap} alignItems="start" w="100%">
        {data?.map((item, index) => (
          <Box key={keyExtractor(item, index)}>{renderItem(item, index)}</Box>
        ))}
      </SimpleGrid>
      {footer}
      <div ref={sentinelRef} />
    </Box>
  );
}
